import {Building2, ChevronRight, FileText, Settings2, Warehouse, type LucideIcon} from "lucide-react";
import {
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem
} from "@/components/ui/sidebar.tsx";
import {NavLink} from "react-router-dom";
import {CollapsibleContent, CollapsibleTrigger, Collapsible} from "@/components/ui/collapsible";

export type NavSubItem = {
    to: string
    label: string
    /** Hidden from the sidebar for any non-Адмін viewer — e.g. "Користувачі",
     *  which mirrors `/subject/users`' own Адмін-only page gate. */
    adminOnly?: boolean
}

export type NavItem = {
    to: string
    label: string
    icon: LucideIcon
    permission?: string;
    children?: NavSubItem[]
}

// app-shell.md's "Робочий простір" group lists 9 items ("Огляд", "Документи
// ОТК", "Розклад", …) — only "Документи ОТК" and "Калібрування друку" have
// real pages behind them in this package so far; the rest land together
// once there's something to link to. "Калібрування друку" itself now lives
// in `settingsNav` below.
export const primaryNav: NavItem[] = [
    {to: "/documents", label: "Документи ОТК", icon: FileText},
    // warehouse.md: "Склад" is a collapsible submenu (same mechanism as
    // "Суб'єкт ОТК" below) with the blanks list as its first child and
    // warehouse-acts.md's read-only archive (`/warehouse/acts`) as its
    // second. warehouse.md also documents this item as visible only in the
    // "Оператор ОТК" menu (hidden for Адмін) — not applied here:
    // `renderNav`/`NavItem` only distinguish admin vs. non-admin today (via
    // `isAdmin`/`adminOnly`), not the three-role split
    // (`ADMIN`/`RESPONSIBLE_PERSON`/`OPERATOR`) this rule needs, and adding
    // that distinction touches every existing nav item, not just this one.
    {
        to: "/warehouse",
        label: "Спецпродукція та Акти",
        icon: Warehouse,
        children: [
            {to: "/warehouse/blanks", label: "Наявна Спецпродукція"},
            {to: "/warehouse/acts", label: "Акти знищення"},
        ],
    },
]

export const settingsNav: NavItem[] = [
    {
        to: "/subject",
        label: "Суб'єкт ОТК",
        icon: Building2,
        children: [
            {to: "/subject/profile", label: "Профіль та Пункти ОТК"},
            {to: "/subject/responsible", label: "Відповідальні особи"},
            {to: "/subject/users", label: "Користувачі", adminOnly: true},
            {to: "/subject/roles", label: "Ролі в АРМ"},
        ],
    },
    // app-shell.md places it in "Налаштування", right after "Суб'єкт ОТК".
    {to: "/print-profiles", label: "Калібрування друку", icon: Settings2},
];

export function renderNav(
    items: NavItem[],
    pathname: string,
    isAdmin: boolean
) {
    return items
        .map(({to, label, icon: Icon, children}) => {
            if (children) {
                const visibleChildren = children.filter(
                    (child) => !child.adminOnly || isAdmin
                )
                if (visibleChildren.length === 0) return null
                // Prefix match against the group's own path (`/subject`), not just
                // its listed children — so a sub-route with no fixed nav entry of
                // its own (e.g. a future `/subject/punkty/:punktId`) still keeps
                // the group expanded and highlighted, per app-shell.md.
                const isChildActive = pathname.startsWith(to)
                return (
                    <Collapsible
                        className="group/collapsible"
                        defaultOpen={isChildActive}
                        key={to}
                    >
                        <SidebarMenuItem>
                            <CollapsibleTrigger asChild>
                                <SidebarMenuButton isActive={isChildActive} tooltip={label}>
                                    <Icon/>
                                    <span>{label}</span>
                                    <ChevronRight
                                        className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90"/>
                                </SidebarMenuButton>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                                <SidebarMenuSub>
                                    {visibleChildren.map((child) => (
                                        <SidebarMenuSubItem key={child.to}>
                                            <SidebarMenuSubButton
                                                asChild
                                                isActive={pathname === child.to}
                                            >
                                                <NavLink to={child.to}>{child.label}</NavLink>
                                            </SidebarMenuSubButton>
                                        </SidebarMenuSubItem>
                                    ))}
                                </SidebarMenuSub>
                            </CollapsibleContent>
                        </SidebarMenuItem>
                    </Collapsible>
                )
            }

            const isActive = to === "/" ? pathname === "/" : pathname.startsWith(to)

            return (
                <SidebarMenuItem key={to}>
                    <SidebarMenuButton asChild isActive={isActive} tooltip={label}>
                        <NavLink to={to}>
                            <Icon/>
                            <span>{label}</span>
                        </NavLink>
                    </SidebarMenuButton>
                </SidebarMenuItem>
            )
        })
}